<template>
  <div>
    <v-chip
      class="job-state-chip"
      :color="isFailed ? 'error' : undefined"
      :variant="isFailed ? 'tonal' : undefined"
      data-testid="job-state-chip"
      :data-failed="isFailed ? 'true' : 'false'"
    >
      <v-avatar left>
        <v-icon v-if="isFailed">fas fa-exclamation-triangle</v-icon>
        <v-icon v-else>fas fa-spinner fa-spin</v-icon>
      </v-avatar>
      <div class="job-state-chip__body">
        <span class="job-state-chip__text" data-testid="job-state-label">{{ state }}</span>
        <v-progress-linear
          v-if="showProgress"
          :value="progressValue"
          height="6"
          rounded
          class="job-state-chip__progress mt-1"
          data-testid="job-state-progress"
        />
      </div>
      <v-avatar
        v-if="hasTrainLog"
        right
        @click.stop="openTrainLog"
        style="cursor: pointer"
        data-testid="job-train-log-btn"
        :title="$t('models_and_train_configs.train_log_title')"
      >
        <v-icon size="large">fas fa-chart-line</v-icon>
      </v-avatar>
      <v-avatar
        v-if="showDelete"
        right
        @click.stop="handleDeleteClick"
        style="cursor: pointer"
        data-testid="job-dismiss-btn"
        :title="isFailed
          ? $t('models_and_train_configs.dismiss_failed_job')
          : $t('models_and_train_configs.cancel_queued_job')"
      >
        <v-icon size="large">far fa-trash-alt</v-icon>
      </v-avatar>
    </v-chip>
    <train-log-modal
      v-if="trainLogOpen"
      :job="job"
      @close="trainLogOpen = false"
    />
  </div>
</template>

<script>
import TrainLogModal from "@/components/models/TrainLogModal";
import {
  jobCanShowTrainLog,
  isFailedTrainJob,
  trainingStateLabel,
  jobDisplayProgress,
} from "@/utils/trainMetrics";

export default {
  name: "JobStateChip",
  components: { TrainLogModal },
  props: {
    job: {required: true}
  },
  data() {
    return {
      trainLogOpen: false,
    };
  },
  computed: {
    description() {
      try {
        return JSON.parse(this.job.description);
      } catch {
        return {};
      }
    },
    metrics() {
      return this.description.train_metrics || {};
    },
    isFailed() {
      return isFailedTrainJob(this.job);
    },
    state() {
      if (this.isFailed) {
        return this.$t('models_and_train_configs.train_failed');
      }
      return trainingStateLabel(this.description, this.metrics);
    },
    progressValue() {
      return jobDisplayProgress(this.job);
    },
    hasTrainLog() {
      return jobCanShowTrainLog(this.job) || this.isFailed;
    },
    showDelete() {
      return this.isFailed || this.state === 'In queue';
    },
    showProgress() {
      return (
        !this.isFailed &&
        this.job.processed === 0 &&
        this.progressValue > 0
      );
    },
  },
  methods: {
    openTrainLog() {
      this.trainLogOpen = true;
    },
    async handleDeleteClick() {
      await this.$api.job.delete(this.job);
    }
  }
}
</script>

<style scoped>
.job-state-chip {
  max-width: 100%;
  height: auto !important;
  white-space: normal;
}
.job-state-chip__text {
  white-space: normal;
  line-height: 1.3;
  padding: 4px 0;
}
/* Failed indication: icon avatars share the chip's red (no white circles). */
.job-state-chip[data-failed='true'] :deep(.v-avatar) {
  background-color: transparent;
}
.job-state-chip[data-failed='true'] :deep(.v-avatar .v-icon) {
  padding: 2px;
}
</style>
